import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { PaginatedResults } from '../../models/paginated_results_model';
import { PaginationOptions } from '../../models/pagination_options';
import { WarehouseTransaction } from '../../models/warehouse-transaction';
import { ClientWarehouseActivityItem } from '../../models/client-warehouse-stock';

@Injectable({
  providedIn: 'root',
})
export class WarehouseTransactionService {
  currentDomain = environment.currentApiHostname
  apiUrl = `https://${this.currentDomain}/api/general`;


  constructor(private http: HttpClient) { }

  /**
   * All warehouse activity for a client — the whole gate + internal movement
   * stream (receiving / dispatch / repack / reconstitution / recovery /
   * transshipment / transfer), newest first. Returns a flat array (NOT
   * paginated). This is stock movement, NOT ledger money.
   * GET /api/general/warehouse-transactions/activity/by-client/{clientUuid}
   */
  activityByClient(clientUuid: string, limit: number = 100) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<Array<ClientWarehouseActivityItem>>(
      this.apiUrl +
      `/warehouse-transactions/activity/by-client/${clientUuid}?limit=${limit}`,
      options
    );
  }

  get(paginationOptions: PaginationOptions) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',

        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<PaginatedResults>(
      this.apiUrl +
      `/warehouse-transactions?${PaginationOptions.getPaginationQueryParameters(
        paginationOptions
      )}`,
      options
    );
  }

  indexByStatus(status: string, paginationOptions: PaginationOptions) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<PaginatedResults>(
      this.apiUrl +
      `/warehouse-transactions/index/status/${status}?${PaginationOptions.getPaginationQueryParameters(
        paginationOptions
      )}`,
      options
    );
  }




  indexByWarehouseTransactionGroup(groupUuid: string, paginationOptions: PaginationOptions) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<PaginatedResults>(
      this.apiUrl +
      `/warehouse-transactions/index/by-warehouse-transaction-group/${groupUuid}?${PaginationOptions.getPaginationQueryParameters(
        paginationOptions
      )}`,
      options
    );
  }


  indexByWarehouse(warehouseUuid: string, paginationOptions: PaginationOptions) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<PaginatedResults>(
      this.apiUrl +
      `/warehouse-transactions/index/by-warehouse/${warehouseUuid}?${PaginationOptions.getPaginationQueryParameters(
        paginationOptions
      )}`,
      options
    );
  }

  all() {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<Array<WarehouseTransaction>>(
      this.apiUrl + `/warehouse-transactions/index/all`,
      options
    );
  }

    getBatchNumbers() {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<Array<string>>(
      this.apiUrl + `/warehouse-transactions/index/batch-numbers`,
      options
    );
  }

  // Warehouse Transactions summary counts for the cards. startDate/endDate are epoch-ms
  // (both-or-none). When present the API ranges on date_created. Bare-object house style.
  summary(startDate?: number | null, endDate?: number | null) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    const params: Array<string> = [];
    if (startDate != null && endDate != null) {
      params.push(`startDate=${startDate}`, `endDate=${endDate}`);
    }
    let url = this.apiUrl + `/warehouse-transactions/index/summary`;
    if (params.length) {
      url += `?${params.join('&')}`;
    }

    return this.http.get<{
      all: number;
      open: number;
      closed: number;
    }>(url, options);
  }

  export(type: string, paginationOptions: PaginationOptions) {
    const options = {
      responseType: 'blob' as 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };


    return this.http.get(
      this.apiUrl +
      `/exports/warehouse-transactions/export/${type}?${PaginationOptions.getPaginationQueryParameters(
        paginationOptions
      )}`,
      options
    );
  }

  // Status + day-range aware export for the Warehouse Transactions tabs. status is one of
  // ALL|OPEN|CLOSED; startDate/endDate/search ride the paginationOptions query string.
  // Returns a Blob (csv/xlsx).
  exportByStatus(type: string, status: string, paginationOptions: PaginationOptions) {
    const options = {
      responseType: 'blob' as 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get(
      this.apiUrl +
      `/exports/warehouse-transactions/export/${type}?status=${status}&${PaginationOptions.getPaginationQueryParameters(
        paginationOptions
      )}`,
      options
    );
  }


  show(uuid: string) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<WarehouseTransaction>(
      this.apiUrl + `/warehouse-transactions/${uuid}`,
      options
    );
  }

  getWarehouseTransactionByTransactionNumber(transactionNumber: string) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<WarehouseTransaction>(
      this.apiUrl + `/warehouse-transactions/get-by-transaction-number/${transactionNumber}`,
      options
    );
  }

  store(category: any) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.post<WarehouseTransaction>(
      this.apiUrl + `/warehouse-transactions`,
      category,
      options
    );
  }

  update(category: any, uuid: string) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.post<WarehouseTransaction>(
      this.apiUrl + `/warehouse-transactions/update/${uuid}`,
      category,
      options
    );
  }



  endorseWarehouseTransaction(category: any, uuid: string) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.post<WarehouseTransaction>(
      this.apiUrl + `/warehouse-transactions/endorse/${uuid}`,
      category,
      options
    );
  }


  /**
   * Closing books the receiving's storage on the client's account, so it names the book (#56). Omitted when there is
   * nothing to book — the request is then the one that has always been sent.
   */
  closeWarehouseTransaction(uuid: string, currency?: string | null) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
      params: (currency ? { currency } : {}) as { [param: string]: string },
    };

    return this.http.get<WarehouseTransaction>(
      this.apiUrl + `/warehouse-transactions/close/${uuid}`,
      options
    );
  }

  /** Whether closing books any storage, and the account holder's book it would follow without a question (#56). */
  chargeBook(uuid: string) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<{ book: string | null; has_charge: boolean }>(
      this.apiUrl + `/warehouse-transactions/index/charge-book/${uuid}`,
      options
    );
  }

  getStockForCommodityInWarehouse(commodityUuid: string, warehouseUuid: string) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<{
      commodity_uuid: string;
      warehouse_uuid: string;
      stock_quantity: number;
    }>(
      this.apiUrl + `/warehouse-transactions/stock/commodity/${commodityUuid}/warehouse/${warehouseUuid}`,
      options
    );
  }

  getStockForCommodityInStack(commodityUuid: string, stackUuid: string) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };

    return this.http.get<{
      commodity_uuid: string;
      stack_uuid: string;
      stock_quantity: number;
    }>(
      this.apiUrl + `/warehouse-transactions/stock/commodity/${commodityUuid}/stack/${stackUuid}`,
      options
    );
  }
}
